import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

// TODO: This should probably be centralized in @attain-apps/entities
const ORG_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.organization.RESOURCE.payload,
)

/** Modal for editing space record functional usage */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
    ds.button.Component,
    ds.notice.Component,
    ds.input.select.single.Component,
    shared.functionalizations.Component,
  ],
  selector: 'atn-space-functional-usage-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.functionalUsage.Plugin} */
  public readonly functionalUsagePlugin = ngCore
    .inject(state.space.full.records.Collection)
    .plugins.lookup(state.space.full.records.plugins.functionalUsage.Plugin)
    .get()

  /** @see {@link state.space.full.warnings.plugin.Plugin} */
  public readonly recordWarningsPlugin = ngCore
    .inject(state.space.full.records.Collection)
    .plugins.lookup(state.space.full.warnings.plugin.Plugin)
    .get()

  /** @see {@link state.space.full.records.Collection} */
  private readonly _recordsEc = ngCore.inject(
    state.space.full.records.Collection,
  )

  /** @see {@link state.space.full.serviceCenters.Collection} */
  private readonly _serviceCentersEc = ngCore.inject(
    state.space.full.serviceCenters.Collection,
  )

  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.space.full.recordAssignedOrgs.Collection} */
  private readonly _orgAssignmentEc = ngCore.inject(
    state.space.full.recordAssignedOrgs.Collection,
  )

  /** @see {@link state.space.full.recordLabAssignments.plugins.functionalUsage.Plugin} */
  public readonly labServiceFunctionalUsagePlugin = ngCore
    .inject(state.space.full.recordLabAssignments.Collection)
    .plugins.lookup(
      state.space.full.recordLabAssignments.plugins.functionalUsage.Plugin,
    )
    .get()

  /** @see {@link state.space.organizations.plugins.jointUse.Plugin} */
  public readonly jointUsePluginSg = ngCore.computed(
    (organizationsEc = this._occupierOrganizationsEcSg()) =>
      organizationsEc?.plugins
        .lookup(state.space.organizations.plugins.jointUse.Plugin)
        .tryGet(),
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** Entity collection for occupier organizations */
  private readonly _occupierOrganizationsEcSg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) => {
      const leafEntity = schema.tryGetLeafEntityOf(ORG_DIMENSION_ID)
      return leafEntity
        ? this._organizationsFeature.selectors
            .getOrganizationEc(leafEntity.id)
            .asSignal()
        : undefined
    },
  )

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** @see {@link ents.extra.space.Warning.Code} */
  public readonly WarningCode = ents.extra.space.Warning.Code

  /** Current Service center ID */
  public readonly serviceCenterIdSg = ngCore.computed(
    (
      editingFunctionalUsage = this.functionalUsagePlugin.selectors.editingFunctionalUsage.asSignal(),
    ) => editingFunctionalUsage?.serviceCenterId,
  )

  /** Current methodology */
  public readonly methodologySg = ngCore.computed(
    (
      editingFunctionalUsage = this.functionalUsagePlugin.selectors.editingFunctionalUsage.asSignal(),
    ) => editingFunctionalUsage?.functionalizationMethodology,
  )

  /** Items for the service center dropdown */
  public readonly serviceCenterItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.ServiceCenter.Id>[]
  >(
    (
      serviceCenters = this._serviceCentersEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) =>
      serviceCenters.map(sc => ({
        label: sc.name,
        value: sc.id,
      })),
  )

  /** Current manual functionalizations being edited */
  public readonly manualFunctionalizationsSg = ngCore.computed(
    (
      editingFunctionalUsage = this.functionalUsagePlugin.selectors.editingFunctionalUsage.asSignal(),
    ) => editingFunctionalUsage?.functionalizations ?? [],
  )

  /** Functionalizations of the currently selected service center */
  public readonly serviceCenterFunctionalizationsSg = ngCore.computed(
    (
      serviceCenterId = this.serviceCenterIdSg(),
      serviceCenter = serviceCenterId
        ? this._serviceCentersEc.plugins.cachePlugin.feature.selectors
            .getCachedEntity(serviceCenterId)
            .asSignal()
        : undefined,
    ) => serviceCenter?.functionalizations ?? [],
  )

  /** {@see {@link ents.extra.space.Functionalization.isEmpty}} */
  private readonly _isEmptySg = ngCore.computed(
    (functionalizations = this.manualFunctionalizationsSg()) =>
      ents.extra.space.Functionalization.isEmpty(functionalizations),
  )

  /** {@see {@link ents.extra.space.Functionalization.isFullyCoded}} */
  private readonly _isFullyCodedSg = ngCore.computed(
    (functionalizations = this.manualFunctionalizationsSg()) =>
      ents.extra.space.Functionalization.isFullyCoded(functionalizations),
  )

  /**
   * Whether the current record is a Lab Service, which is the only room type
   * category for which the Auto Lab Based methodology is valid.
   */
  public readonly isLabServiceSg = ngCore.computed(
    (
      currentRecord = this._recordsEc.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal(),
    ) =>
      currentRecord?.roomTypeCategory ===
      ents.extra.space.RoomType.Category.LabService,
  )

  /**
   * Methodology items for select - options derived from {@link ents.extra.space.Record.FunctionalUsage.Methodology} enum.
   * The Auto Lab Based option is only offered for Lab Service records.
   */
  public readonly methodologyItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.Record.FunctionalUsage.Methodology>[]
  >((isLabService = this.isLabServiceSg()) =>
    ents.extra.space.Record.FunctionalUsage.Methodology.ALL.filter(
      methodology =>
        isLabService ||
        methodology !==
          ents.extra.space.Record.FunctionalUsage.Methodology.AutoLabBased,
    ).map(methodology => ({
      label: methodology,
      value: methodology,
    })),
  )

  /** Whether the Save button should be enabled */
  public readonly canConfirmSg = ngCore.computed(
    (
      isEmpty = this._isEmptySg(),
      isFullyCoded = this._isFullyCodedSg(),
      isServiceCenter = this.isServiceCenterMethodologySg(),
      isJointUse = this.isJointUseMethodologySg(),
      isAutoLabBased = this.isAutoLabBasedMethodologySg(),
      serviceCenterId = this.serviceCenterIdSg(),
      occupierOrg = this.occupierOrganizationSg(),
    ) => {
      if (isServiceCenter) {
        return TypeChecker.isNotNullish(serviceCenterId)
      }

      if (isJointUse) {
        return TypeChecker.isNotNullish(occupierOrg)
      }

      if (isAutoLabBased) {
        // Allocations are auto-calculated from assigned labs; no input required.
        return true
      }

      return isEmpty || isFullyCoded
    },
  )

  /** Whether to display functionalizations section */
  public readonly isManualMethodologySg = ngCore.computed(
    (methodology = this.methodologySg()) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.Manual,
  )

  /** Whether to display service center dropdown */
  public readonly isServiceCenterMethodologySg = ngCore.computed(
    (methodology = this.methodologySg()) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.ServiceCenter,
  )

  /** Whether to display joint-use functionalizations */
  public readonly isJointUseMethodologySg = ngCore.computed(
    (methodology = this.methodologySg()) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.JointUse,
  )

  /** Whether the Auto Lab Based methodology is selected */
  public readonly isAutoLabBasedMethodologySg = ngCore.computed(
    (methodology = this.methodologySg()) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.AutoLabBased,
  )

  /**
   * The ID of the occupier's organization
   */
  public readonly occupierOrganizationIdSg = ngCore.computed(
    (
      orgAssignments = this._orgAssignmentEc.plugins.cachePlugin.feature.selectors
        .getCachedEntities()
        .asSignal(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => {
      const leafEntity = schema.tryGetLeafEntityOf(ORG_DIMENSION_ID)
      if (!leafEntity) {
        return undefined
      }
      const occupier = orgAssignments.find(
        orgAssignment => orgAssignment?.isOccupier,
      )
      return occupier?.organizationLookup[leafEntity.id]
    },
  )

  /**
   * The occupier's organization
   */
  public readonly occupierOrganizationSg = ngCore.computed(
    (
      occupierOrganizationId = this.occupierOrganizationIdSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => {
      const leafEntity = schema.tryGetLeafEntityOf(ORG_DIMENSION_ID)
      return occupierOrganizationId && leafEntity
        ? this._organizationsFeature.selectors
            .getOrganizationEc(leafEntity.id)
            .asSignal()
            ?.plugins.cachePlugin.feature.selectors.getCachedEntity(
              occupierOrganizationId,
            )
            .asSignal()
        : undefined
    },
  )

  /**
   * When the user switches to JointUse methodology and there is an occupier
   * organization, load joint-use functionalizations.
   */
  private readonly _fetchJointUseFunctionalizations = ngCore.effect(() => {
    const isUpdating =
      this.functionalUsagePlugin.selectors.isUpdating.asSignal()
    const isJointUse = this.isJointUseMethodologySg()
    const org = this.occupierOrganizationSg()
    const jointUsePlugin = this.jointUsePluginSg()

    if (TypeChecker.isNullish(jointUsePlugin)) {
      return
    }

    if (isUpdating && isJointUse && TypeChecker.isNotNullish(org)) {
      jointUsePlugin.feature.actions.setCurrentEntityTarget.request
        .from(
          'Space.RecordDetails.FunctionalUsageModal.JointUseMethodologySelected',
        )
        .dispatch({
          id: org.id,
        })
    } else {
      jointUsePlugin.feature.actions.releaseCurrentEntityTarget.request
        .from(
          'Space.RecordDetails.FunctionalUsageModal.JointUseMethodologyLeft',
        )
        .dispatch()
    }
  })

  /** Update the selected service center */
  public updateServiceCenterHandler(
    serviceCenterId: ents.extra.space.ServiceCenter.Id | undefined,
  ): void {
    this.functionalUsagePlugin.actions.update
      .from('Space.RecordDetails.FunctionalUsageModal.ServiceCenterChanged')
      .dispatch({
        serviceCenterId,
      })
  }

  /**
   * Update a functionalization
   */
  public updateFunctionalization(
    functionalization: ents.extra.space.Functionalization,
  ): void {
    this.functionalUsagePlugin.actions.updateFunctionalization
      .from('Space.RecordDetails.FunctionalUsageModal.FunctionalizationChanged')
      .dispatch(functionalization)
  }

  /**
   * Update the methodology
   */
  public updateFunctionalizationMethodology(
    functionalizationMethodology: string | undefined,
  ): void {
    ents.extra.space.Record.FunctionalUsage.Methodology.assertIsMethodology(
      functionalizationMethodology,
    )
    this.functionalUsagePlugin.actions.update
      .from('Space.RecordDetails.FunctionalUsageModal.MethodologyChanged')
      .dispatch({
        functionalizationMethodology,
      })
  }

  /** Cancel edit modal */
  public cancelClickHandler(): void {
    this.functionalUsagePlugin.actions.discard
      .from('Space.RecordDetails.FunctionalUsageModal.Cancel.Clicked')
      .dispatch()
  }

  /** Save changes */
  public saveClickHandler(): void {
    this.functionalUsagePlugin.actions.applyUpdates
      .from('Space.RecordDetails.FunctionalUsageModal.Save.Clicked')
      .dispatch()
  }
}
